// packages/frontend/src/components/auth/LogoutNotification.tsx

import { useToast } from '../../contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { Coffee, Rocket, Cookie } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import i18n from '../../i18n/i18n';

// Global toast manager for calling from non-React contexts
let globalToastManager: {
  addToast: (toast: Omit<import('../ui/Toast').Toast, 'id'>) => string;
} | null = null;

/**
 * Set the global toast manager (called by ToastProvider on mount)
 */
export function setGlobalToastManager(manager: typeof globalToastManager) {
  globalToastManager = manager;
}

/**
 * Helper function to show a persistent, humorous logout notification
 * This can be called from anywhere (React components or non-React contexts)
 */
export function showLogoutNotification() {
  if (!globalToastManager) {
    // Fallback: dispatch a custom event that can be listened to
    window.dispatchEvent(new CustomEvent('show-logout-notification'));
    return;
  }

  // Use i18n directly for translations
  const t = (key: string) => i18n.t(key);

  const messages = [
    {
      icon: Coffee,
      message: t('common:logoutNotification.messages.coffee'),
    },
    {
      icon: Rocket,
      message: t('common:logoutNotification.messages.adventure'),
    },
    {
      icon: Cookie,
      message: t('common:logoutNotification.messages.snack'),
    },
  ];

  const randomMessage = messages[Math.floor(Math.random() * messages.length)];

  globalToastManager.addToast({
    type: 'warning',
    title: t('common:logoutNotification.title'),
    message: randomMessage.message,
    persistent: true,
    closable: true,
    position: 'top-center',
    action: {
      label: t('common:logoutNotification.acknowledge'),
      onClick: () => {
        // Notification dismissed
      },
    },
  });
}

/**
 * LogoutNotification Component
 * Displays a persistent, eye-catching notification when user is logged out
 */
interface LogoutNotificationProps {
  onAcknowledge?: () => void;
}

export function LogoutNotification({ onAcknowledge }: LogoutNotificationProps) {
  const { addToast } = useToast();
  const { t } = useTranslation(['common']);

  // Random humorous messages
  const messages = useMemo(() => [
    {
      icon: Coffee,
      message: t('common:logoutNotification.messages.coffee'),
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
    },
    {
      icon: Rocket,
      message: t('common:logoutNotification.messages.adventure'),
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    {
      icon: Cookie,
      message: t('common:logoutNotification.messages.snack'),
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
    },
  ], [t]);

  const randomMessage = useMemo(() => {
    return messages[Math.floor(Math.random() * messages.length)];
  }, [messages]);

  useEffect(() => {
    addToast({
      type: 'warning',
      title: t('common:logoutNotification.title'),
      message: randomMessage.message,
      persistent: true,
      closable: true,
      position: 'top-center',
      action: {
        label: t('common:logoutNotification.acknowledge'),
        onClick: () => {
          if (onAcknowledge) {
            onAcknowledge();
          }
        },
      },
      onClose: () => {
        if (onAcknowledge) {
          onAcknowledge();
        }
      },
    });

    return () => {
      // Cleanup if component unmounts
    };
  }, [addToast, randomMessage, t, onAcknowledge]);

  return null; // This component doesn't render anything itself
}

/**
 * Hook to show logout notification programmatically
 * Use this from non-React contexts or when you need to trigger it manually
 */
export function useLogoutNotification() {
  const { addToast } = useToast();
  const { t } = useTranslation(['common']);

  const showNotification = () => {
    const messages = [
      {
        icon: Coffee,
        message: t('common:logoutNotification.messages.coffee'),
        color: 'text-amber-600',
      },
      {
        icon: Rocket,
        message: t('common:logoutNotification.messages.adventure'),
        color: 'text-blue-600',
      },
      {
        icon: Cookie,
        message: t('common:logoutNotification.messages.snack'),
        color: 'text-orange-600',
      },
    ];

    const randomMessage = messages[Math.floor(Math.random() * messages.length)];

    addToast({
      type: 'warning',
      title: t('common:logoutNotification.title'),
      message: randomMessage.message,
      persistent: true,
      closable: true,
      position: 'top-center',
      action: {
        label: t('common:logoutNotification.acknowledge'),
        onClick: () => {
          // Notification dismissed
        },
      },
    });
  };

  return { showLogoutNotification: showNotification };
}

